import { InventoryPanel, type InventoryItemData } from '@repo/ui'
import React, { useMemo } from 'react'
import { useClientSideTranslation } from 'src/services/i18n'
import { useGetCurrentLanguage, useGetCurrentGame, useGetGameConfig } from 'src/store/game'
import { isMarbleGame } from 'src/types/domain/Game'
import { isKnownGameConfig } from 'src/types/domain/GameConfig'
import { useInventoryItems, useInventoryStore } from '../../store'
import { useInventoryItemActions } from '../../hooks'
import { mapInventoryItemToItemData } from '../../mappers'
import { isItemApplicableToGame } from '../../utils/isItemApplicableToGame'
import { getApplicableGamesText } from '../../utils/getApplicableGamesText'


type InventoryPanelWrapperProps = Readonly<{
  onClose?: () => void
  setBetAmount?: (betAmount: string) => void
}>

export function InventoryPanelWrapper({ onClose, setBetAmount }: InventoryPanelWrapperProps) {
  const { t } = useClientSideTranslation(useGetCurrentLanguage())
  const inventoryItems = useInventoryItems()
  const activatedItemIds = useInventoryStore((state) => state.activatedItemIds)
  const { handleActivateItem, handleActivateBonus, handleDeactivateItem } = useInventoryItemActions(setBetAmount)
  const currentGame = useGetCurrentGame()
  const currentGameId = isMarbleGame(currentGame) ? currentGame.id : ''
  const gameConfig = useGetGameConfig()

  // Memoize the context to prevent unnecessary recalculations
  const contextBase = useMemo(() => ({
    t,
    onActivateItem: handleActivateItem,
    onActivateBonus: handleActivateBonus,
    onDeactivateItem: handleDeactivateItem
  }), [t, handleActivateItem, handleActivateBonus, handleDeactivateItem])

  const formattedItems = useMemo(() => {
    return inventoryItems.map(item => {
      const isApplicableToGame = currentGameId ? isItemApplicableToGame(item, currentGameId) : true
      const applicableGamesText = getApplicableGamesText(item, isApplicableToGame, t)
      
      const context = {
        ...contextBase,
        applicableGamesText,
        isApplicableToGame,
        minBetAmount: isKnownGameConfig(gameConfig) ? gameConfig.bet.minBetAmount : undefined
      }
      
      return mapInventoryItemToItemData(item, context, activatedItemIds, inventoryItems)
    })
  }, [inventoryItems, activatedItemIds, currentGameId, contextBase, gameConfig])

  return (
    <InventoryPanel
      items={formattedItems.filter(Boolean) as InventoryItemData[]}
      onClose={onClose}
      subtitle={t('Tap on an item to use')}
      title={t('Inventory')}
      tooltip={t('Your collected items and bonuses')}
    />
  )
}